import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, startTransition } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Box,
	Card,
	FormControlLabel,
	Grid,
	Stack,
	Switch,
	Tooltip,
	Typography
} from '@mui/material';
import { DataGridPro, GRID_DETAIL_PANEL_TOGGLE_COL_DEF, useGridApiRef } from '@mui/x-data-grid-pro';
import { AppTheme } from '../../../common/theme';
import { useQuoteHeader } from '../quoteHeader/customHooks';
import { processAuditTrailRows } from './auditTrailClientFilters';
import {
	buildAuditTrailRequestPayload,
	cloneAppliedFilters,
	areAuditTrailFiltersEqual,
	loadSavedAuditTrailFilters,
	parseAuditTrailRows,
	saveAuditTrailFilters
} from './auditTrailFilterUtils';
import AuditTrailFilterBar from './AuditTrailFilterBar.jsx';
import {
	auditLogGridToggleButtonSx,
	formatActorDisplayName,
	formatEventDateCell,
	formatQuoteVersionDisplay,
	getEventCategoryDisplay,
	getEventDisplayText,
	getEventSourceDisplay,
	getQuoteTotalDisplay
} from './auditTrailUtils';
import {
	AUDIT_TRAIL_EVENT_COLUMN_MIN_WIDTH,
	AUDIT_TRAIL_SOURCE_COLUMN_WIDTH,
	AUDIT_TRAIL_GRID_HEIGHT,
	DEFAULT_PAGE_SIZE,
	NO_RECORDS_FOUND,
	SHOW_LOW_PRIORITY_EVENTS_LABEL,
	SHOW_LOW_PRIORITY_EVENTS_TOOLTIP
} from './constants';
import { QuoteAuditChangeDetails, getQuoteAuditDetailPanelHeight } from './QuoteAuditChangeDetails';
import { getAuditTrails } from './services';

const renderEllipsisCell = (text, title) => {
	const displayText = text || '';

	if (!displayText) {
		return '';
	}

	const tooltipTitle = title ?? displayText;

	return (
		<Tooltip title={tooltipTitle} disableInteractive>
			<Typography
				variant="body2"
				noWrap
				sx={{
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					width: '100%',
					minWidth: 0,
					lineHeight: 'inherit'
				}}
			>
				{displayText}
			</Typography>
		</Tooltip>
	);
};

const renderTruncatedEventCell = (params) => renderEllipsisCell(params.value);

const renderQuoteTotalCell = (params, formatCurrency) => {
	const display = getQuoteTotalDisplay(params.row, formatCurrency);

	if (!display?.value) {
		return '';
	}

	if (display.isLegacyOneTimeOnly && display.tooltip) {
		return (
			<Tooltip title={display.tooltip}>
				<span>{display.value}</span>
			</Tooltip>
		);
	}

	return display.value;
};

const getInitialAuditTrailFilters = () => loadSavedAuditTrailFilters();

/** DataGrid expand/focus can scroll the window; restore after React commits the panel. */
const lockWindowScrollForRestore = (scrollLockRef) => {
	scrollLockRef.current = { x: window.scrollX, y: window.scrollY };
};

const AUDIT_TRAIL_ROW_HEIGHT = 52;

const DETAIL_PANEL_TOGGLE_FIELD = GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field;

const resetDetailPanelLayout = (apiRef) => {
	const api = apiRef.current;

	if (!api) {
		return;
	}

	api.requestPipeProcessorsApplication?.('rowHeight');
	api.forceUpdate?.();
};

const AuditTrail = () => {
	const [allAuditRows, setAllAuditRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [appliedFilters, setAppliedFilters] = useState(getInitialAuditTrailFilters);
	const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState([]);
	const [detailPanelWidth, setDetailPanelWidth] = useState(null);
	const gridContainerRef = useRef(null);
	const resizeObserverRef = useRef(null);
	const pendingWindowScrollRestoreRef = useRef(null);
	const gridApiRef = useGridApiRef();
	const { currentQuote: quote } = useQuoteHeader();
	const quoteReadableId = quote?.QuoteReadableId;

	const auditTrailArray = useMemo(
		() => processAuditTrailRows(allAuditRows, appliedFilters),
		[allAuditRows, appliedFilters]
	);

	const updateDetailPanelWidth = useCallback(() => {
		if (!gridContainerRef.current) {
			return;
		}

		const width = gridContainerRef.current.clientWidth;
		setDetailPanelWidth((previousWidth) => (previousWidth === width ? previousWidth : width));
	}, []);

	useEffect(() => {
		if (!gridContainerRef.current || typeof ResizeObserver === 'undefined') {
			updateDetailPanelWidth();
			return undefined;
		}

		updateDetailPanelWidth();
		resizeObserverRef.current = new ResizeObserver(() => {
			updateDetailPanelWidth();
		});
		resizeObserverRef.current.observe(gridContainerRef.current);

		return () => {
			if (resizeObserverRef.current) {
				resizeObserverRef.current.disconnect();
				resizeObserverRef.current = null;
			}
		};
	}, [updateDetailPanelWidth]);

	useEffect(() => {
		if (detailPanelExpandedRowIds.length > 0) {
			updateDetailPanelWidth();
		}
	}, [detailPanelExpandedRowIds, updateDetailPanelWidth]);

	useLayoutEffect(() => {
		const pending = pendingWindowScrollRestoreRef.current;

		if (!pending) {
			return;
		}

		pendingWindowScrollRestoreRef.current = null;

		if (window.scrollX !== pending.x || window.scrollY !== pending.y) {
			window.scrollTo(pending.x, pending.y);
		}
	}, [detailPanelExpandedRowIds]);

	useEffect(() => {
		if (detailPanelExpandedRowIds.length === 0) {
			requestAnimationFrame(() => resetDetailPanelLayout(gridApiRef));
		}
	}, [detailPanelExpandedRowIds, gridApiRef]);

	const formatCurrency = useCallback(
		(value) => (quote?.formatCurrency ? quote.formatCurrency(value) : String(value ?? '')),
		[quote]
	);

	useEffect(() => {
		if (detailPanelExpandedRowIds.length === 0) {
			return;
		}

		const rowId = detailPanelExpandedRowIds[0];
		const rowIndex = auditTrailArray.findIndex(
			(row) => String(row.IdQuoteAuditTrail) === String(rowId)
		);

		if (rowIndex < 0) {
			return;
		}

		const scrollExpandedRowIntoView = () => {
			if (gridApiRef.current?.scrollToIndexes) {
				gridApiRef.current.scrollToIndexes({ rowIndex, colIndex: 0 });
			}
		};

		requestAnimationFrame(scrollExpandedRowIntoView);
	}, [detailPanelExpandedRowIds, auditTrailArray, gridApiRef]);

	useEffect(() => {
		setPage(0);
		setDetailPanelExpandedRowIds([]);
	}, [quoteReadableId, appliedFilters]);

	useEffect(() => {
		if (!quoteReadableId) {
			setAllAuditRows([]);
			return undefined;
		}

		let cancelled = false;
		setLoading(true);

		const loadAuditTrailData = async () => {
			const payload = buildAuditTrailRequestPayload({ quoteReadableId });
			const response = await getAuditTrails(payload);

			if (cancelled) {
				return;
			}

			const { rows } = parseAuditTrailRows(response?.response0);
			setAllAuditRows(rows);
			setLoading(false);
			setDetailPanelExpandedRowIds([]);
		};

		loadAuditTrailData();

		return () => {
			cancelled = true;
		};
	}, [quoteReadableId]);

	const handleFiltersCommit = useCallback((nextFilters) => {
		const committed = cloneAppliedFilters(nextFilters);

		if (areAuditTrailFiltersEqual(committed, appliedFilters)) {
			return;
		}

		saveAuditTrailFilters(committed);
		startTransition(() => {
			setAppliedFilters(committed);
			setPage(0);
			setDetailPanelExpandedRowIds([]);
		});
	}, [appliedFilters]);

	const handleIncludeSystemChange = useCallback((event) => {
		handleFiltersCommit({
			...appliedFilters,
			includeSystem: event.target.checked
		});
	}, [appliedFilters, handleFiltersCommit]);

	const handlePaginationChange = useCallback((model) => {
		setPage(model.page);
		setDetailPanelExpandedRowIds([]);
	}, []);

	const handleDetailPanelExpandedRowIdsChange = useCallback((ids) => {
		const expandedIds = ids || [];
		let nextIds = expandedIds;

		if (expandedIds.length > 1) {
			const previousIds = detailPanelExpandedRowIds;
			const newlyExpandedId = expandedIds.find((id) => !previousIds.some((previousId) => previousId === id));

			nextIds = newlyExpandedId != null ? [newlyExpandedId] : [expandedIds[expandedIds.length - 1]];
		}

		lockWindowScrollForRestore(pendingWindowScrollRestoreRef);
		setDetailPanelExpandedRowIds(nextIds);

		if (nextIds.length === 0) {
			requestAnimationFrame(() => resetDetailPanelLayout(gridApiRef));
		}
	}, [detailPanelExpandedRowIds, gridApiRef]);

	const toggleDetailPanelRow = useCallback((rowId) => {
		const isExpanded = detailPanelExpandedRowIds.some((id) => id === rowId);
		lockWindowScrollForRestore(pendingWindowScrollRestoreRef);
		setDetailPanelExpandedRowIds(isExpanded ? [] : [rowId]);
	}, [detailPanelExpandedRowIds]);

	const handleCellClick = useCallback((params, event) => {
		if (params.field !== DETAIL_PANEL_TOGGLE_FIELD) {
			return;
		}

		event.stopPropagation();
		toggleDetailPanelRow(params.id);
	}, [toggleDetailPanelRow]);

	const handleRowClick = useCallback((params, event) => {
		if (event.target.closest(`[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"]`)) {
			return;
		}

		if (event.target.closest('button')) {
			return;
		}

		toggleDetailPanelRow(params.id);
	}, [toggleDetailPanelRow]);

	const getDetailPanelContent = useCallback(
		(params) => (
			<Box
				sx={{
					width: detailPanelWidth ? `${detailPanelWidth}px` : '100%',
					maxWidth: '100%',
					minWidth: 0,
					boxSizing: 'border-box'
				}}
			>
				<QuoteAuditChangeDetails row={params.row} formatCurrency={formatCurrency} />
			</Box>
		),
		[detailPanelWidth, formatCurrency]
	);

	const getDetailPanelHeight = useCallback(
		(params) => getQuoteAuditDetailPanelHeight(params?.row, formatCurrency),
		[formatCurrency]
	);

	const columns = useMemo(
		() => [
			{
				...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
				align: 'left',
				headerAlign: 'left',
				width: 52,
				minWidth: 52,
				maxWidth: 52,
				flex: 0
			},
			{
				field: 'EventDate',
				headerName: 'Date',
				disableColumnMenu: true,
				width: 180,
				minWidth: 180,
				maxWidth: 180,
				flex: 0,
				valueGetter: (value) => formatEventDateCell(value),
				valueFormatter: (value) => value?.display || '',
				sortComparator: (v1, v2) => (v1?.raw || 0) - (v2?.raw || 0)
			},
			{
				field: 'EventCategory',
				headerName: 'Category',
				disableColumnMenu: true,
				width: 120,
				minWidth: 120,
				maxWidth: 120,
				flex: 0,
				valueGetter: (value, row) => getEventCategoryDisplay(row),
				renderCell: (params) => renderEllipsisCell(params.value)
			},
			{
				field: 'EventSource',
				headerName: 'Source',
				disableColumnMenu: true,
				width: AUDIT_TRAIL_SOURCE_COLUMN_WIDTH,
				minWidth: AUDIT_TRAIL_SOURCE_COLUMN_WIDTH,
				maxWidth: AUDIT_TRAIL_SOURCE_COLUMN_WIDTH,
				flex: 0,
				valueGetter: (value, row) => getEventSourceDisplay(row),
				renderCell: (params) => renderEllipsisCell(params.value)
			},
			{
				field: 'EventInfo',
				headerName: 'Event',
				disableColumnMenu: true,
				minWidth: AUDIT_TRAIL_EVENT_COLUMN_MIN_WIDTH,
				flex: 1,
				valueGetter: (value, row) => getEventDisplayText(row),
				renderCell: renderTruncatedEventCell
			},
			{
				field: 'User',
				headerName: 'User',
				disableColumnMenu: true,
				width: 160,
				minWidth: 160,
				maxWidth: 160,
				flex: 0,
				valueGetter: (value, row) => formatActorDisplayName(row),
				renderCell: (params) => renderEllipsisCell(params.value)
			},
			{
				field: 'QuoteVersion',
				headerName: 'Version',
				disableColumnMenu: true,
				width: 110,
				minWidth: 110,
				maxWidth: 110,
				flex: 0,
				valueGetter: (value, row) => formatQuoteVersionDisplay(row)
			},
			{
				field: 'QuoteTotal',
				headerName: 'Total Value',
				disableColumnMenu: true,
				width: 140,
				minWidth: 140,
				maxWidth: 140,
				flex: 0,
				valueGetter: (value, row) => row?.QuoteTotal ?? row?.quote_total ?? null,
				valueFormatter: (value, row) => getQuoteTotalDisplay(row, formatCurrency)?.value || '',
				renderCell: (params) => renderQuoteTotalCell(params, formatCurrency)
			}
		],
		[formatCurrency]
	);

	return (
		<AppTheme>
			<Card sx={{ ml: '5px', my: '5px' }}>
				<Grid container sx={{ px: 2, py: 1 }}>
					<Grid item xs={12}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								flexWrap: 'wrap',
								gap: 1,
								py: 1
							}}
						>
							<Typography sx={{ fontSize: '15px', fontWeight: 'bold' }}>
								Quote Audit Trail
							</Typography>
							<Tooltip title={SHOW_LOW_PRIORITY_EVENTS_TOOLTIP} disableInteractive>
								<span style={{ display: 'inline-flex' }}>
									<FormControlLabel
										sx={{ m: 0 }}
										control={
											<Switch
												checked={appliedFilters.includeSystem}
												onChange={handleIncludeSystemChange}
												color="primary"
												size="small"
											/>
										}
										label={SHOW_LOW_PRIORITY_EVENTS_LABEL}
									/>
								</span>
							</Tooltip>
						</Box>
					</Grid>
					<Grid
						item
						xs={12}
						sx={{
							width: '100%',
							padding: '0 0 16px'
						}}
					>
					<AuditTrailFilterBar
						appliedFilters={appliedFilters}
						onCommit={handleFiltersCommit}
					/>
					<Box
						ref={gridContainerRef}
						sx={{
							width: '100%',
							maxWidth: '100%',
							minWidth: 0,
							height: AUDIT_TRAIL_GRID_HEIGHT,
							minHeight: AUDIT_TRAIL_GRID_HEIGHT,
							pb: 1,
							overflow: 'hidden',
							boxSizing: 'border-box'
						}}
					>
						<DataGridPro
							apiRef={gridApiRef}
							data-testid="test-audit-trail-grid"
							height={AUDIT_TRAIL_GRID_HEIGHT}
							disableVirtualization
							disableColumnResize
							density="compact"
							columns={columns}
							rows={auditTrailArray}
							getRowId={(row) => row.IdQuoteAuditTrail}
							getRowHeight={() => AUDIT_TRAIL_ROW_HEIGHT}
							loading={loading}
							pagination
							paginationMode="client"
							paginationModel={{ page, pageSize: DEFAULT_PAGE_SIZE }}
							pageSizeOptions={[DEFAULT_PAGE_SIZE]}
							onPaginationModelChange={handlePaginationChange}
							onRowClick={handleRowClick}
							onCellClick={handleCellClick}
							detailPanelExpandedRowIds={detailPanelExpandedRowIds}
							onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
							disableRowSelectionOnClick
							disableMultipleRowSelection
							getDetailPanelContent={getDetailPanelContent}
							getDetailPanelHeight={getDetailPanelHeight}
							slots={{
								detailPanelExpandIcon: ChevronRightIcon,
								detailPanelCollapseIcon: ExpandMoreIcon,
								noRowsOverlay: () => (
									<Stack
										data-testid="test-audit-trail-grid-no-data"
										height="100%"
										alignItems="center"
										justifyContent="center"
									>
										{!loading && NO_RECORDS_FOUND || ''}
									</Stack>
								)
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton'
								},
								cell: {
									tabIndex: -1
								},
								baseIconButton: {
									disableRipple: true,
									disableFocusRipple: true,
									tabIndex: -1,
									sx: auditLogGridToggleButtonSx
								}
							}}
							initialState={{
								sorting: {
									sortModel: [{ field: 'EventDate', sort: 'desc' }]
								}
							}}
							sx={(theme) => ({
								height: AUDIT_TRAIL_GRID_HEIGHT,
								minHeight: AUDIT_TRAIL_GRID_HEIGHT,
								width: '100%',
								maxWidth: '100%',
								minWidth: 0,
								overflow: 'hidden',
								borderColor: theme.palette.quote_step.lockedFontColor,
								'& .MuiDataGrid-columnHeaderTitle': {
									color: 'primary.main',
									fontWeight: 'bold'
								},
								'& .MuiDataGrid-main': {
									width: '100%',
									maxWidth: '100%'
								},
								'& .MuiDataGrid-virtualScroller': {
									overflowX: 'auto',
									overflowY: 'auto'
								},
								'& .MuiDataGrid-columnHeaders': {
									maxWidth: '100%'
								},
								'& .MuiDataGrid-detailPanel': {
									left: 0,
									overflow: 'hidden',
									backgroundColor: theme.palette.background.default,
									color: theme.palette.text.primary,
									borderTop: `1px solid ${theme.palette.divider}`
								},
								'& .MuiDataGrid-detailPanels': {
									overflow: 'hidden'
								},
								'& .MuiDataGrid-detailPanelToggleCell': {
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-start',
									overflow: 'hidden',
									px: 0.5,
									cursor: 'pointer'
								},
								[`& .MuiDataGrid-cell[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"]`]: {
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-start',
									overflow: 'hidden',
									px: 0.5,
									cursor: 'pointer'
								},
								[`& .MuiDataGrid-cell[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"] button`]: {
									...auditLogGridToggleButtonSx,
									pointerEvents: 'none'
								},
								[`& .MuiDataGrid-columnHeader[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"]`]: {
									px: 0.5
								}
							})}
						/>
					</Box>
				</Grid>
			</Grid>
			</Card>
		</AppTheme>
	);
};

export default AuditTrail;
